<template>
     <div class="nibnut">
        <div class="nibnut-app">
            <app-header
                :is-public-page="is_public_page"
                @new-project="new_project"
                @new-contact="new_contact"
                @new-user="new_user"
            />
            <div
                :class="{ [$route.name]: true }"
                class="body"
            >
                <main>
                    <router-view></router-view>
                </main>

                <base-button
                    v-if="!!profile&&profile.is_db_admin"
                    :class="{'uk-active': !!profile&&!!profile.is_db_admin_active}"
                    class="uk-icon-button uk-icon-key lam-admin-toggle"
                    @click.prevent="toggle_admin_mode"
                ></base-button>
                <small v-if="!!app_version" class="lam-app-version">{{ app_version }}</small>
            </div>

            <confirmation
                v-if="confirming"
                v-bind="confirmation_properties"
                @done="done_confirming"
            />
        </div>

        <new-project
            :show.sync="creating_project"
            :internal="creating_project_type === 'overhead_project'"
        />
        <new-contact
            :show.sync="creating_contact"
            @created="$router.push({ name: 'contact.edit', params: { id: $event.id }})"
        />
        <new-user
            :show.sync="creating_user"
        />
        <modal-dialog
            id="message-board"
            :show="!!message.id"
            :dismissable="false"
        >
            <div v-html="message.message" class="uk-text-large"></div>

            <div class="uk-modal-footer uk-text-center">
                <base-button
                    size="large"
                    @click.prevent="next_message"
                >
                    OK
                </base-button>
            </div>
        </modal-dialog>
        <authentication />
        <transition name="flip" enter-active-class="animated flipInX" leave-active-class="animated flipOutX" mode="out-in">
            <div
                v-if="!!system_message.message"
                @click="dismiss_message"
                :class="{ [`uk-alert-${system_message.type}`]: true }"
                class="uk-alert system-message"
            >
                <a href="#" class="uk-alert-close uk-close"></a>
                <div>
                    <div>{{ system_message.message }}</div>
                </div>
                <!--
                <progress
                    v-if="!!message_countdown"
                    :value="message_countdown"
                    :max="system_message.dismiss_after"
                    class="progress"
                ></progress>
                //-->
            </div>
        </transition>
    </div>
</template>

<script type="text/javascript">
import { mapGetters, mapState } from "vuex"

import profile_utilities from "./mixins/ProfileUtilities"
import ui_utilities from "./mixins/UiUtilities"
import string_utilities from "./mixins/StringUtilities"

import AppHeader from "./AppHeader"
import BaseButton from "./ui/BaseButton"
import ModalDialog from "./dialogs/ModalDialog/ModalDialog"
import Confirmation from "./dialogs/ModalDialog/Confirmation"

import Authentication from "./dialogs/Authentication/Authentication"
import NewUser from "./dialogs/NewUser"
import NewProject from "./dialogs/NewProject"
import NewContact from "./dialogs/NewContact"

let timer = null

export default {
    mixins: [profile_utilities, ui_utilities, string_utilities],
    components: {
        AppHeader,
        BaseButton,
        ModalDialog,
        Confirmation,
        Authentication,
        NewUser,
        NewProject,
        NewContact
    },
    mounted () {
        this.load()
    },
    watch: {
        "system_message.message": "system_message_changed",
        lockdown: "lockdown_changed"
    },
    methods: {
        load () {
            this.$store.dispatch("LOAD_PROFILE").catch(() => {
                // ignore any error
            })
        },
        done_confirming (confirmed) {
            if(this.confirmation_props && this.confirmation_props.then) this.confirmation_props.then(confirmed)
            this.$store.dispatch("CONFIRM", { confirming: false, props: {} })
        },
        system_message_changed () {
            if(timer) {
                clearInterval(timer)
                timer = null
            }
            if(this.system_message.message) {
                this.message_countdown = this.system_message.dismiss_after || 7000
                const step = 10
                timer = setInterval(() => {
                    this.message_countdown -= step
                    if(this.message_countdown <= 0) {
                        clearInterval(timer)
                        timer = null
                        this.dismiss_message()
                    }
                }, step)
            }
        },
        dismiss_message (event) {
            const system_message = this.system_message
            const system_message_id = system_message ? system_message.message_id : null
            this.$store.dispatch("SYSTEM_MESSAGE", {
                type: "warning",
                message: "",
                dismiss_after: 0,
                message_id: null
            })
            if(!!event && !!system_message_id && (system_message_id === "update-app")) this.update_app()
        },
        new_project (type) {
            this.creating_project_type = type
            this.creating_project = true
        },
        new_contact () {
            this.creating_contact = true
        },
        new_user () {
            this.creating_user = true
        },

        toggle_admin_mode () {
            this.$store.dispatch("TOGGLE_ADMIN_MODE").then(() => {
                // Weird css bug, the style wouldn't be applied?
                this.fresh_css = false
                this.$nextTick(() => { this.fresh_css = true })
            })
        },
        lockdown_changed (value) {
            if(this.locking_down && !this.ticker) {
                this.ticker_time = this.$moment.duration(this.lockdown.valueOf() - this.$moment().valueOf(), "milliseconds")
                this.ticker = window.setInterval(() => {
                    this.ticker_time = this.$moment.duration(this.lockdown.valueOf() - this.$moment().valueOf(), "milliseconds")
                }, 1000)
            } else if(!this.locking_down && this.ticker) {
                window.clearInterval(this.ticker)
                this.ticker_time = null
                this.ticker = null
            }
        },
        next_message () {
            const message = this.message
            if(message.id) {
                this.$store.dispatch("RECORD_SAVE", {
                    entity: "message",
                    id: message.id,
                    data: { read_at: this.$moment().format("YYYY-MM-DD HH:mm:ss") }
                })
            }
        }
    },
    computed: {
        ...mapState(["lockdown", "confirming", "confirmation_props", "system_message"]),
        ...mapGetters(["entity_records"]),
        application_unlocked () {
            return !!this.profile_id && !this.locked_down
        },
        locking_down () {
            return this.lockdown && this.lockdown.isAfter()
        },
        locking_down_in () {
            if(this.locking_down && this.ticker_time) {
                const milliseconds = this.ticker_time.asMilliseconds()
                if(milliseconds >= 0) return this.$moment(milliseconds).format("mm:ss")
            }
            return "00:00"
        },
        locked_down () {
            return this.lockdown && this.lockdown.isBefore()
        },
        confirmation_properties () {
            const { then, ...confirmation_properties } = this.confirmation_props
            return confirmation_properties
        },
        message () {
            return this.entity_records("message").find(message => {
                return (message.recipient_id === this.profile_id) && !message.read_at
            }) || { id: 0 }
        }
    },
    data () {
        return {
            message_countdown: 0,

            creating_project: false,
            creating_project_type: "",

            creating_contact: false,

            creating_user: false,

            fresh_css: true,

            ticker: null,
            ticker_time: null
        }
    }
}
</script>

<style lang="less">
    .uk-alert.system-message {
        position: fixed;
        left: 0;
        top: 0;
        width: 100%;
        height: 60px;
        box-sizing: border-box;
        z-index: 5000;
        text-align: center;
        margin: 0;
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity .3s ease;
    }
    .fade-enter, .fade-leave-active {
        opacity: 0;
    }
</style>
